export type ContactPointKind = 'EMAIL' | 'PHONE';

export type ContactPoint = {
  id?: number;
  kind: ContactPointKind;
  value: string;
  label?: string | null;
  contactName?: string | null;
  contactPosition?: string | null;
  contactGroupKey?: string | null;
  isPrimary?: boolean;
  sortOrder?: number;
};

export type ContactPointRow = {
  id?: number;
  value: string;
  label: string;
  contactName?: string;
  contactPosition?: string;
  isPrimary: boolean;
};

export const EMAIL_TYPE_OPTIONS = [
  { value: 'Work', label: 'Work' },
  { value: 'Personal', label: 'Personal' },
  { value: 'Billing', label: 'Billing' },
  { value: 'Other', label: 'Other' },
];

export const PHONE_TYPE_OPTIONS = [
  { value: 'Work', label: 'Work' },
  { value: 'Mobile', label: 'Mobile' },
  { value: 'Home', label: 'Home' },
  { value: 'Fax', label: 'Fax' },
  { value: 'Other', label: 'Other' },
];

export function emptyContactPointRow(kind: ContactPointKind, isPrimary = false): ContactPointRow {
  return {
    value: '',
    label: kind === 'EMAIL' ? 'Work' : 'Mobile',
    contactName: '',
    contactPosition: '',
    isPrimary,
  };
}

export function sortContactPoints(points: ContactPoint[], kind: ContactPointKind): ContactPoint[] {
  return points
    .filter((point) => point.kind === kind && point.value?.trim())
    .sort((a, b) => {
      if (a.isPrimary && !b.isPrimary) return -1;
      if (!a.isPrimary && b.isPrimary) return 1;
      return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
}

export function getContactPointValues(points: ContactPoint[] | undefined, kind: ContactPointKind): string[] {
  return sortContactPoints(points ?? [], kind).map((point) => point.value.trim());
}

export function contactPointsFilterText(points: ContactPoint[] | undefined, kind: ContactPointKind): string {
  return sortContactPoints(points ?? [], kind)
    .map((point) =>
      [point.contactName, point.contactPosition, point.value]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(' '),
    )
    .join(' ')
    .toLowerCase();
}

export function hasContactPoints(points: ContactPoint[] | undefined, kind: ContactPointKind): boolean {
  return (points ?? []).some((point) => point.kind === kind && point.value?.trim());
}

export function contactPointsToRows(
  points: ContactPoint[] | undefined,
  kind: ContactPointKind,
): ContactPointRow[] {
  return sortContactPoints(points ?? [], kind).map((point) => ({
    id: point.id,
    value: point.value,
    label: point.label || (kind === 'EMAIL' ? 'Work' : 'Mobile'),
    contactName: point.contactName ?? '',
    contactPosition: point.contactPosition ?? '',
    isPrimary: Boolean(point.isPrimary),
  }));
}

export function rowsToContactPoints(
  rows: ContactPointRow[] | undefined,
  kind: ContactPointKind,
  person?: { contactName?: string; contactPosition?: string },
): ContactPoint[] {
  const cleaned = (rows ?? [])
    .map((row, index) => ({
      id: row.id,
      kind,
      value: row.value.trim(),
      label: row.label?.trim() || (kind === 'EMAIL' ? 'Work' : 'Mobile'),
      contactName: person?.contactName?.trim() || row.contactName?.trim() || undefined,
      contactPosition: person?.contactPosition?.trim() || row.contactPosition?.trim() || undefined,
      isPrimary: Boolean(row.isPrimary),
      sortOrder: index,
    }))
    .filter((row) => row.value !== '');

  if (cleaned.length === 0) return [];

  const hasPrimary = cleaned.some((row) => row.isPrimary);
  if (!hasPrimary) cleaned[0].isPrimary = true;

  let primaryAssigned = false;
  return cleaned.map((row, index) => {
    const isPrimary = row.isPrimary && !primaryAssigned;
    if (isPrimary) primaryAssigned = true;
    return { ...row, isPrimary, sortOrder: index };
  });
}

export function mergeContactPointPayload(
  contactEmails?: ContactPointRow[],
  contactPhones?: ContactPointRow[],
): ContactPoint[] {
  return [
    ...rowsToContactPoints(contactEmails, 'EMAIL'),
    ...rowsToContactPoints(contactPhones, 'PHONE'),
  ];
}

export function getPrimaryContactPoint(points: ContactPoint[] | undefined, kind: ContactPointKind): ContactPoint | undefined {
  const filtered = (points ?? []).filter((point) => point.kind === kind && point.value?.trim());
  return filtered.find((point) => point.isPrimary) ?? filtered[0];
}

export function formatContactPointValue(point: ContactPoint): string {
  const label = point.label?.trim();
  return label ? `${label}: ${point.value}` : point.value;
}

export function formatContactPointLine(point: ContactPoint): string {
  const personParts = [point.contactName?.trim(), point.contactPosition?.trim()].filter(Boolean);
  const person = personParts.length > 0 ? personParts.join(' · ') : '';
  const valuePart = formatContactPointValue(point);
  return person ? `${person} — ${valuePart}` : valuePart;
}
